import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import BackgroundCheckOverride from '@app/models-internal/background-check-override';
import BackgroundCheckStepCompletedArgs from '@app/models-internal/background-check-step-completed-args';
import AdminBackgroundCheck from '@app/models/admin-background-check';
import AdminBackgroundCheckSummary from '@app/models/admin-background-check-summary';
import CandidateViewModel from '@app/models/candidate-view-model';
import CheckrBackgroundCheckGridRowViewModel from '@app/models/checkr-background-check-grid-row-view-model';
import { Observable, Subject, throwError as observableThrowError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { BackgroundCheckWithAuth } from '../models';
import { FileDownloadService } from '@oa/shared-core';

@Injectable()
export class BackgroundCheckService {
  private readonly _serviceName = 'BackgroundCheckService';

  public stepCompleted = new Subject<BackgroundCheckStepCompletedArgs>();

  private _candidate: any;

  constructor(private http: HttpClient, private fileDownloadService: FileDownloadService) {}

  /**
   * Gets the background check status for the current authenticated user
   */
  getStatus() {
    // 
    const failedCancelledOrPending = ['Cancelled', 'Failed', 'Under Review', 'fail', 'consider', 'pending', 'Fail', 'suspended', 'Cancel'];

    return this.http.get<string>('BackgroundCheck/Status').pipe(
      map((status) => ({
        status,
        get isSuccess() {
          for (let index = 0; index < failedCancelledOrPending.length; index++) {
            if (failedCancelledOrPending[index] === status.toString()) {
              return false;
            }
          }
          return true;
        },
      }))
    );
  }

  /**
   * Checks to see if the user has a special override to proceed w/ a background check
   */
  public hasBackgroundCheckNonMemberOverride(userId: number) {
    return this.http.get<boolean>(`BackgroundCheck/allow/${userId}`);
  }

  /*
   * Broadcasts the step name that is complete--stepCompleted subscribers will receive the completed step name
   */
  public completeStep(args: BackgroundCheckStepCompletedArgs) {
    console.log(`${this._serviceName}().completeStep args=`, args);

    this.stepCompleted.next(args);
  }

  validatePassword(password: string) {
    return this.http.post('BackgroundCheck/ValidatePassword', JSON.stringify(password)).pipe(
      catchError((error) => {
        return observableThrowError(error && typeof error.json === 'function' ? error.json() : error);
      })
    );
  }

  /**
   * Stores a background candidate while additional data is obtained.
   * Mechanism by which candidate information is shared between components
   * before it is POSTed to the API endpoint.
   * @param candidate
   */
  updateCandidate(candidate: any) {
    this._candidate = candidate;
  }

  /**
   * Gets a background candidate
   * @param candidate
   */
  getCandidate() {
    return this._candidate;
  }

  /**
   * Gets background check candidate data for a user id
   * @param userId
   */
  getBackgroundCheckCandidate(userId: number) {
    if (!userId || userId == 0) throw new Error('userId must be greater than zero.');

    return this.http.get<CandidateViewModel>(`BackgroundCheck/candidate/${userId}`);
  }

  /**
   * Submits a user's SSN for a background check.
   * @param ssn
   */
  submit(data: BackgroundCheckWithAuth) {
    return this.http.post('BackgroundCheck/V3', data).pipe(
      catchError((error) => {
        return observableThrowError(error && typeof error.json === 'function' ? error.json() : error);
      })
    );
  }

  /**
   * Downloads the actual auhtorization document as BLOB
   * @param userId
   */
  getAuthorizationDocument(userId: number) {
    var url = `BackgroundCheck/AuthorizationDocument/${userId}`;

    return this.fileDownloadService.downloadFile(url);
  }

  /**
   * Gets a background report document
   * @param providerType
   * @param reportId
   */
  getBackgroundReportDocument(providerType: string, reportId: string) {
    var url = `BackgroundCheck/Document/${providerType}/${reportId}`;

    return this.fileDownloadService.downloadFile(url);
  }

  /**
   * Does the user have an authorization document for their background check?
   * @param userId
   */
  hasAuthorizationDocument(userId: number): Observable<boolean> {
    var url = `BackgroundCheck/HasAuthorizationDocument/${userId}`;

    return this.http.get<boolean>(url);
  }

  getAdministrativeBackgroundChecks(userId: number) {
    if (!userId) {
      throw new Error('getAdministrativeBackgroundChecks requires a userId parameter.');
    }

    if (userId === 0) {
      throw new Error(`${userId} is an invalid userId value.`);
    }

    var url = `BackgroundCheck/admin/${userId}`;

    return this.http.get<AdminBackgroundCheck[]>(url);
  }

  /**
   * Gets an array of unclean background check data for the admin checkr background check
   */
  getUncleanBackgroundChecks() {
    var url = 'BackgroundCheck/admin/unclean';

    return this.http.get<CheckrBackgroundCheckGridRowViewModel[]>(url);
  }

  getAdministrativeBackgroundCheckSummary(userId: number) {
    if (!userId) {
      throw new Error('getAdministrativeBackgroundCheckSummary requires a userId parameter.');
    }

    if (userId === 0) {
      throw new Error(`${userId} is an invalid userId value.`);
    }

    var url = `BackgroundCheck/admin/summary/${userId}`;

    return this.http.get<AdminBackgroundCheckSummary[]>(url);
  }

  setOverride(overrideModel: BackgroundCheckOverride) {
    return this.http.post<BackgroundCheckOverride>('BackgroundCheck/admin/override', overrideModel);
  }

  removeOverride(userId: number) {
    if (!userId) {
      throw new Error('getAdministrativeBackgroundChecks requires a userId parameter.');
    }

    if (userId === 0) {
      throw new Error(`${userId} is an invalid userId value.`);
    }

    var url = `BackgroundCheck/admin/override/${userId}`;

    return this.http.delete(url);
  }

  getOverride(userId: number) {
    if (!userId) {
      throw new Error('getOverride requires a userId parameter.');
    }

    if (userId === 0) {
      throw new Error(`${userId} is an invalid userId value.`);
    }

    var url = `BackgroundCheck/admin/override/${userId}`;

    return this.http.get<BackgroundCheckOverride>(url);
  }
}
